// src/pages/CompanyManagement/tabs/LocationsTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Divider,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Room as RoomIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const LocationsTab = ({ companyId, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  useEffect(() => {
    loadLocations();
  }, [companyId]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API_URL}/api/companies/${companyId}/locations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const locationTree = response.data.data.tree || [];
        setLocations(locationTree);
        // Auto-expand root nodes
        const rootExpanded = {};
        locationTree.forEach(loc => {
          rootExpanded[loc.id] = true;
        });
        setExpandedNodes(rootExpanded);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_load_locations'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const getLocationIcon = (type) => {
    const icons = {
      building: <BusinessIcon />,
      floor: <HomeIcon />,
      room: <RoomIcon />
    };
    return icons[type] || <LocationIcon />;
  };

  const filterLocations = (locations, searchTerm) => {
    if (!Array.isArray(locations)) return [];
    if (!searchTerm) return locations;

    const search = searchTerm.toLowerCase();

    const filterRecursive = (locs) => {
      if (!Array.isArray(locs)) return [];

      return locs.filter(loc => {
        const matches = loc.name?.toLowerCase().includes(search) ||
                       loc.locationTypeId?.toLowerCase().includes(search);

        const filteredChildren = loc.children ? filterRecursive(loc.children) : [];

        return matches || filteredChildren.length > 0;
      }).map(loc => ({
        ...loc,
        children: loc.children ? filterRecursive(loc.children) : []
      }));
    };

    return filterRecursive(locations);
  };

  const countTotalLocations = (locations) => {
    if (!Array.isArray(locations)) return 0;

    let count = 0;
    const countRecursive = (locs) => {
      if (!Array.isArray(locs)) return;

      locs.forEach(loc => {
        count++;
        if (loc.children) {
          countRecursive(loc.children);
        }
      });
    };
    countRecursive(locations);
    return count;
  };

  const renderLocationTree = (locations, level = 0) => {
    if (!Array.isArray(locations)) return null;

    return locations.map((location) => {
      const hasChildren = location.children && location.children.length > 0;
      const isExpanded = expandedNodes[location.id];

      return (
        <Box key={location.id}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.75,
              pl: level * 3,
              '&:hover': {
                bgcolor: 'action.hover',
                cursor: 'pointer'
              },
              borderRadius: 1
            }}
            onClick={() => hasChildren && toggleNode(location.id)}
          >
            <Box sx={{ width: 24, display: 'flex', alignItems: 'center' }}>
              {hasChildren ? (
                <IconButton size="small" sx={{ p: 0 }}>
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              ) : (
                <Box sx={{ width: 24 }} />
              )}
            </Box>

            <Box sx={{ ml: 1, mr: 1, display: 'flex', color: 'text.secondary' }}>
              {getLocationIcon(location.locationTypeId)}
            </Box>

            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
              {location.name}
            </Typography>

            <Chip
              label={location.locationTypeId || 'unknown'}
              size="small"
              variant="outlined"
              sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
            />

            {hasChildren && (
              <Chip
                label={t('CompanyManagement.sub_locations', { count: location.children.length, plural: location.children.length !== 1 ? 's' : '' })}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderLocationTree(location.children, level + 1)}
            </Collapse>
          )}
        </Box>
      );
    });
  };

  const filteredLocations = filterLocations(locations, searchTerm);
  const totalCount = countTotalLocations(locations);
  const filteredCount = countTotalLocations(filteredLocations);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('CompanyManagement.locations_title')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.locations_tree', { count: filteredCount })}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('CompanyManagement.locations_tree_info')}
          </Alert>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('CompanyManagement.search_locations')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          {/* Location Tree */}
          {filteredLocations.length === 0 ? (
            <Alert severity="info">
              {searchTerm ? t('CompanyManagement.no_locations_search') : t('CompanyManagement.no_locations')}
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 500, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {renderLocationTree(filteredLocations)}
            </Box>
          )}

          {/* Summary */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {searchTerm
                ? t('CompanyManagement.locations_summary_filtered', {
                    total: totalCount,
                    plural: totalCount !== 1 ? 's' : '',
                    filtered: filteredCount,
                    pluralFiltered: filteredCount !== 1 ? 's' : ''
                  })
                : t('CompanyManagement.locations_summary', {
                    total: totalCount,
                    plural: totalCount !== 1 ? 's' : ''
                  })
              }
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LocationsTab;
